/**
 * Servicio de Actividades del Evento.
 *
 * Modelo:
 *  - `eventActivityCategories`  → categorías por evento (con rubric default).
 *  - `eventActivities`          → actividades dentro de una categoría.
 *  - `clubActivityScores`       → resultado de un club en una actividad.
 *
 * IDs determinísticos:
 *  - Categoría:  `${eventId}_${slug}`
 *  - Actividad:  `${eventId}_${slug}` (actSlug es único por evento)
 *  - Score:      `${eventId}_${activityId}_${clubId}`
 */

import {
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  where,
} from "firebase/firestore";
import { db, auth } from "@/lib/firebase";
import { subscribeToCollection } from "./base";
import {
  SUBMISSION_TEMPLATES,
  type SubmissionTemplateKey,
} from "./activitySubmissions";
import type {
  ActivityScoreConfig,
  ActivityScoreInput,
  ActivityScoreMode,
  ClubActivityScore,
  EventActivity,
  EventActivityCategory,
  PodiumRubric,
} from "@/types";

const CAT_COLLECTION = "eventActivityCategories";
const ACT_COLLECTION = "eventActivities";
const SCORE_COLLECTION = "clubActivityScores";

// ============================================================================
// Helpers
// ============================================================================

function slugify(input: string): string {
  return input
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

export function buildCategoryId(eventId: string, slug: string): string {
  return `${eventId}_${slug}`;
}

export function buildActivityId(eventId: string, slug: string): string {
  return `${eventId}_${slug}`;
}

export function buildScoreId(
  eventId: string,
  activityId: string,
  clubId: string,
): string {
  return `${eventId}_${activityId}_${clubId}`;
}

function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = {};
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    if (v === undefined) return;
    if (
      v &&
      typeof v === "object" &&
      !Array.isArray(v) &&
      !(v instanceof Date)
    ) {
      out[k] = stripUndefined(v as Record<string, unknown>);
    } else {
      out[k] = v;
    }
  });
  return out as T;
}

// ============================================================================
// Categorías
// ============================================================================

export function subscribeToCategories(
  eventId: string,
  cb: (items: EventActivityCategory[]) => void,
  onError?: (e: Error) => void,
): () => void {
  return subscribeToCollection<EventActivityCategory>(
    CAT_COLLECTION,
    (items) => cb([...items].sort((a, b) => a.order - b.order)),
    [where("eventId", "==", eventId)],
    onError,
  );
}

export interface UpsertCategoryInput {
  eventId: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  order: number;
  defaultScoreMode: ActivityScoreMode;
  defaultScoreConfig: ActivityScoreConfig;
  active?: boolean;
  /** Si se pasa, conserva el slug (modo edición). */
  slug?: string;
}

export async function upsertCategory(
  input: UpsertCategoryInput,
): Promise<string> {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("No autenticado");
  const slug = input.slug ?? slugify(input.name);
  if (!slug) throw new Error("Nombre inválido para categoría");
  const id = buildCategoryId(input.eventId, slug);
  const ref = doc(db, CAT_COLLECTION, id);
  await setDoc(
    ref,
    stripUndefined({
      id,
      eventId: input.eventId,
      name: input.name.trim(),
      slug,
      description: input.description,
      color: input.color,
      icon: input.icon,
      order: input.order,
      defaultScoreMode: input.defaultScoreMode,
      defaultScoreConfig: input.defaultScoreConfig,
      active: input.active ?? true,
      updatedAt: serverTimestamp(),
      updatedBy: uid,
      createdAt: serverTimestamp(),
      createdBy: uid,
    }),
    { merge: true },
  );
  return id;
}

export async function deleteCategory(categoryId: string): Promise<void> {
  await deleteDoc(doc(db, CAT_COLLECTION, categoryId));
}

// ============================================================================
// Actividades
// ============================================================================

export function subscribeToActivities(
  eventId: string,
  cb: (items: EventActivity[]) => void,
  onError?: (e: Error) => void,
): () => void {
  return subscribeToCollection<EventActivity>(
    ACT_COLLECTION,
    (items) =>
      cb(
        [...items].sort(
          (a, b) =>
            a.categoryId.localeCompare(b.categoryId) || a.order - b.order,
        ),
      ),
    [where("eventId", "==", eventId)],
    onError,
  );
}

export interface UpsertActivityInput {
  eventId: string;
  categoryId: string;
  name: string;
  description?: string;
  longDescription?: string;
  scoreMode: ActivityScoreMode;
  scoreConfig: ActivityScoreConfig;
  participants: EventActivity["participants"];
  order: number;
  active?: boolean;
  tags?: string[];
  slug?: string;
  submissionRequirements?: EventActivity["submissionRequirements"];
}

export async function upsertActivity(
  input: UpsertActivityInput,
): Promise<string> {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("No autenticado");
  const slug = input.slug ?? slugify(input.name);
  if (!slug) throw new Error("Nombre inválido para actividad");
  const id = buildActivityId(input.eventId, slug);
  const ref = doc(db, ACT_COLLECTION, id);
  await setDoc(
    ref,
    stripUndefined({
      id,
      eventId: input.eventId,
      categoryId: input.categoryId,
      name: input.name.trim(),
      slug,
      description: input.description,
      longDescription: input.longDescription,
      scoreMode: input.scoreMode,
      scoreConfig: input.scoreConfig,
      participants: input.participants,
      order: input.order,
      active: input.active ?? true,
      tags: input.tags,
      submissionRequirements: input.submissionRequirements,
      updatedAt: serverTimestamp(),
      updatedBy: uid,
      createdAt: serverTimestamp(),
      createdBy: uid,
    }),
    { merge: true },
  );
  return id;
}

export async function deleteActivity(activityId: string): Promise<void> {
  await deleteDoc(doc(db, ACT_COLLECTION, activityId));
}

// ============================================================================
// Resultados de clubes
// ============================================================================

export function subscribeToScoresByEvent(
  eventId: string,
  cb: (items: ClubActivityScore[]) => void,
  onError?: (e: Error) => void,
): () => void {
  return subscribeToCollection<ClubActivityScore>(
    SCORE_COLLECTION,
    cb,
    [where("eventId", "==", eventId)],
    onError,
  );
}

/**
 * Calcula los puntos de un club en una actividad a partir del input del
 * jurado y la configuración de puntuación.
 */
export function computePoints(
  mode: ActivityScoreMode,
  config: ActivityScoreConfig,
  input: ActivityScoreInput,
): number {
  switch (mode) {
    case "podium": {
      const r: PodiumRubric = config.podium ?? {
        first: 0,
        second: 0,
        third: 0,
        participation: 0,
      };
      switch (input.position) {
        case "first":
          return r.first;
        case "second":
          return r.second;
        case "third":
          return r.third;
        case "participation":
          return r.participation;
        default:
          return 0;
      }
    }
    case "pass_fail":
      return input.passed ? (config.passFailValue ?? 0) : 0;
    case "fixed":
      return input.delivered ? (config.fixedValue ?? 0) : 0;
    case "deductive": {
      const base = config.deductiveBase ?? 0;
      const ded = input.deductionPoints ?? 0;
      return Math.max(0, base - ded);
    }
    case "accumulative": {
      const base = config.accumulativeBase ?? 0;
      const perUnit = config.accumulativePerUnit ?? 0;
      const penalty = config.accumulativePenaltyPerUnit ?? 0;
      const units = input.unitsCompleted ?? 0;
      const pen = input.penaltyUnits ?? 0;
      return Math.max(0, base + perUnit * units - penalty * pen);
    }
    case "timing": {
      const bands = config.timingBands ?? [];
      const idx = input.timingBandIndex ?? -1;
      return bands[idx]?.points ?? 0;
    }
    default:
      return 0;
  }
}

export interface UpsertScoreInput {
  eventId: string;
  activityId: string;
  activityName: string;
  categoryId: string;
  categoryName: string;
  clubId: string;
  clubName: string;
  scoreMode: ActivityScoreMode;
  scoreConfig: ActivityScoreConfig;
  input: ActivityScoreInput;
  notes?: string;
  scoredByName: string;
}

export async function upsertScore(input: UpsertScoreInput): Promise<string> {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("No autenticado");
  const id = buildScoreId(input.eventId, input.activityId, input.clubId);
  const points = computePoints(input.scoreMode, input.scoreConfig, input.input);
  const ref = doc(db, SCORE_COLLECTION, id);
  await setDoc(
    ref,
    stripUndefined({
      id,
      eventId: input.eventId,
      activityId: input.activityId,
      activityName: input.activityName,
      categoryId: input.categoryId,
      categoryName: input.categoryName,
      clubId: input.clubId,
      clubName: input.clubName,
      input: input.input,
      points,
      notes: input.notes,
      scoredById: uid,
      scoredByName: input.scoredByName,
      scoredAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: uid,
      createdAt: serverTimestamp(),
      createdBy: uid,
    }),
    { merge: true },
  );
  return id;
}

export async function deleteScore(scoreId: string): Promise<void> {
  await deleteDoc(doc(db, SCORE_COLLECTION, scoreId));
}

// ============================================================================
// Seed: rubrics estándar y categorías iniciales basadas en boletín FIEL
// CONQUISTADOR / Asoriente.
// ============================================================================

export const STANDARD_RUBRICS: Record<string, PodiumRubric> = {
  espirituales: {
    first: 10000,
    second: 7000,
    third: 5000,
    participation: 2000,
  },
  formacion: { first: 10000, second: 7000, third: 5000, participation: 2000 },
  deportivos: { first: 7000, second: 5000, third: 3000, participation: 1000 },
  carpeta: { first: 10000, second: 7000, third: 5000, participation: 2000 },
};

export interface StandardCategorySeed {
  name: string;
  slug: string;
  color: string;
  icon: string;
  defaultScoreMode: ActivityScoreMode;
  defaultScoreConfig: ActivityScoreConfig;
  order: number;
  description?: string;
}

export const STANDARD_CATEGORY_SEEDS: StandardCategorySeed[] = [
  {
    name: "Espirituales",
    slug: "espirituales",
    color: "#7c3aed",
    icon: "BookOpen",
    order: 10,
    defaultScoreMode: "podium",
    defaultScoreConfig: { podium: STANDARD_RUBRICS.espirituales },
    description: "Eventos espirituales: matutina, conexión bíblica, etc.",
  },
  {
    name: "Formación",
    slug: "formacion",
    color: "#0ea5e9",
    icon: "ShieldCheck",
    order: 20,
    defaultScoreMode: "podium",
    defaultScoreConfig: { podium: STANDARD_RUBRICS.formacion },
    description: "Uniforme, marchas, conservación ambiental, etc.",
  },
  {
    name: "Deportivos",
    slug: "deportivos",
    color: "#16a34a",
    icon: "Trophy",
    order: 30,
    defaultScoreMode: "podium",
    defaultScoreConfig: { podium: STANDARD_RUBRICS.deportivos },
    description: "Voleibol, ultimate, natación, pickleball, etc.",
  },
  {
    name: "Preparación y Misión",
    slug: "preparacion",
    color: "#f59e0b",
    icon: "Flame",
    order: 40,
    defaultScoreMode: "fixed",
    defaultScoreConfig: { fixedValue: 5000 },
    description: "Eventos previos: conquistador por un día, etc.",
  },
  {
    name: "Carpeta del Club",
    slug: "carpeta",
    color: "#9333ea",
    icon: "Folder",
    order: 50,
    defaultScoreMode: "podium",
    defaultScoreConfig: { podium: STANDARD_RUBRICS.carpeta },
    description: "Calificación de la carpeta del club.",
  },
  {
    name: "Informe Mensual",
    slug: "informe-mensual",
    color: "#0891b2",
    icon: "ClipboardList",
    order: 60,
    defaultScoreMode: "timing",
    defaultScoreConfig: {
      timingBands: [
        { label: "A tiempo (máx. día 5)", points: 2000 },
        { label: "Extra tiempo (día 6-10)", points: 1500 },
        { label: "Tarde", points: 0 },
      ],
    },
    description: "Reporte de eventos previos, abril a junio.",
  },
  {
    name: "Cumplimiento y Organización",
    slug: "cumplimiento",
    color: "#10b981",
    icon: "CheckCircle2",
    order: 70,
    defaultScoreMode: "pass_fail",
    defaultScoreConfig: { passFailValue: 5000 },
    description: "Pago de inscripciones, seguros y soportes al día.",
  },
  {
    name: "Disciplina",
    slug: "disciplina",
    color: "#ef4444",
    icon: "AlertTriangle",
    order: 80,
    defaultScoreMode: "deductive",
    defaultScoreConfig: { deductiveBase: 10000 },
    description: "Inicia con 10.000 pts; se descuenta por faltas.",
  },
  {
    name: "Reunión de Directores",
    slug: "reunion-directores",
    color: "#6366f1",
    icon: "Users",
    order: 90,
    defaultScoreMode: "accumulative",
    defaultScoreConfig: {
      accumulativeBase: 8000,
      accumulativePerUnit: 2000,
      accumulativePenaltyPerUnit: 500,
    },
    description: "Base 8.000 + 2.000 por reunión − 500 por llegada tarde.",
  },
  {
    name: "Video Eventos Previos",
    slug: "video-previos",
    color: "#db2777",
    icon: "Video",
    order: 100,
    defaultScoreMode: "fixed",
    defaultScoreConfig: { fixedValue: 10000 },
    description: "Puntaje fijo si se entrega; suma al total general.",
  },
];

/**
 * Crea las categorías estándar si aún no existen para el evento.
 * Devuelve los IDs creados/existentes.
 */
export async function seedStandardCategories(
  eventId: string,
): Promise<string[]> {
  const ids: string[] = [];
  for (const seed of STANDARD_CATEGORY_SEEDS) {
    const id = await upsertCategory({
      eventId,
      name: seed.name,
      slug: seed.slug,
      color: seed.color,
      icon: seed.icon,
      order: seed.order,
      defaultScoreMode: seed.defaultScoreMode,
      defaultScoreConfig: seed.defaultScoreConfig,
      description: seed.description,
      active: true,
    });
    ids.push(id);
  }
  return ids;
}

// ============================================================================
// Labels para UI
// ============================================================================

export const SCORE_MODE_LABEL: Record<ActivityScoreMode, string> = {
  podium: "Podio (1°/2°/3°/Participación)",
  pass_fail: "Cumple / No cumple",
  fixed: "Puntaje fijo si entrega",
  deductive: "Inicial menos descuentos",
  accumulative: "Base + acumulado − penalización",
  timing: "Por fecha de entrega",
};

export const POSITION_LABEL: Record<string, string> = {
  first: "1° lugar",
  second: "2° lugar",
  third: "3° lugar",
  participation: "Participación",
};

// ============================================================================
// Importación desde CSV
// ============================================================================

/**
 * Formato CSV (separador `,`, comillas dobles para campos con comas):
 *
 *   tipo,categoria,nombre,modo,puntos,descripcion,color,orden
 *
 * Donde:
 *  - `tipo` = `C` (categoría) | `A` (actividad)
 *  - `categoria` = nombre de la categoría (siempre obligatorio)
 *  - `nombre` = nombre de la actividad (solo para tipo A)
 *  - `modo` = uno de podium | pass_fail | fixed | deductive | accumulative | timing
 *             En tipo A puede ir vacío y se hereda de la categoría.
 *  - `puntos` (formato según modo, separados por `;`):
 *       podium       → 1°;2°;3°;Participación  (4 números)
 *       pass_fail    → 5000
 *       fixed        → 5000
 *       deductive    → 10000  (base inicial)
 *       accumulative → base;perUnit;penaltyPerUnit  (3 números)
 *       timing       → Etiqueta1:1000;Etiqueta2:500;Tarde:0
 *                      o con fechas tope:
 *                      Anticipada@2026-05-03 23:59:2500;A tiempo@2026-05-05 23:59:2000;Tarde:500
 *                      (la primera banda cuya fecha tope sea ≥ ahora se aplica;
 *                       la última sin fecha es el fallback "tarde")
 *  - `descripcion`, `color`, `orden` = opcionales.
 */
export const CSV_TEMPLATE_HEADER =
  "tipo,categoria,nombre,modo,puntos,descripcion,color,orden,entregable,plantilla,apertura,deadline,permite_tarde,notificar";

export const CSV_TEMPLATE_EXAMPLE = `${CSV_TEMPLATE_HEADER}
C,Espirituales,,podium,10000;7000;5000;2000,Eventos espirituales,#7c3aed,10,,,,,
A,Espirituales,Matutina,,,Asistencia diaria,,10,,,,,
A,Espirituales,Conexión bíblica,,,,,20,,,,,
C,Deportivos,,podium,7000;5000;3000;1000,,#16a34a,30,,,,,
A,Deportivos,Voleibol,,,,,10,,,,,
A,Deportivos,Ultimate,,,,,20,,,,,
C,Cumplimiento,,pass_fail,5000,,#10b981,70,,,,,
A,Cumplimiento,Pago al día,,,,,10,,,,,
C,Disciplina,,deductive,10000,,#ef4444,80,,,,,
A,Disciplina,Comportamiento,,,,,10,,,,,
C,Reunión Directores,,accumulative,8000;2000;500,,#6366f1,90,,,,,
A,Reunión Directores,Asistencia reuniones,,,,,10,,,,,
C,Informe Mensual,,timing,A tiempo:2000;Extra tiempo:1500;Tarde:0,,#0891b2,60,,,,,
A,Informe Mensual,Reporte abril,,,,,10,1,informe_mensual,2026-05-01 00:00,2026-05-05 23:59,0
A,Informe Mensual,Reporte mayo,,,,,20,1,informe_mensual,2026-06-01 00:00,2026-06-05 23:59,0
A,Informe Mensual,Reporte junio,,,,,30,1,informe_mensual,2026-06-10 00:00,2026-06-15 23:59,0
C,Documentos,,fixed,0,Subidas informativas (sin puntaje),#64748b,100,,,,,
A,Documentos,Menú del campamento,,,Menú balanceado del club,,10,1,single_file,,2026-06-15 23:59,1`;

const VALID_MODES: ActivityScoreMode[] = [
  "podium",
  "pass_fail",
  "fixed",
  "deductive",
  "accumulative",
  "timing",
];

/**
 * Parsea una línea CSV respetando comillas dobles ("...") y comas dentro
 * de comillas. Soporta `""` como escape de comilla dentro de un campo.
 */
function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ",") {
        out.push(cur);
        cur = "";
      } else {
        cur += ch;
      }
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

function parseNumber(s: string): number {
  const n = Number(s.replace(/[\s_]/g, "").replace(",", "."));
  if (!Number.isFinite(n)) throw new Error(`Número inválido: "${s}"`);
  return n;
}

function buildScoreConfig(
  mode: ActivityScoreMode,
  puntos: string,
): ActivityScoreConfig {
  const raw = puntos.trim();
  switch (mode) {
    case "podium": {
      const parts = raw
        .split(";")
        .map((p) => p.trim())
        .filter(Boolean);
      if (parts.length < 3) {
        throw new Error(
          `Modo podium requiere al menos 3 valores (1°;2°;3°[;Participación]): "${raw}"`,
        );
      }
      return {
        podium: {
          first: parseNumber(parts[0]),
          second: parseNumber(parts[1]),
          third: parseNumber(parts[2]),
          participation: parts[3] ? parseNumber(parts[3]) : 0,
        },
      };
    }
    case "pass_fail":
      return { passFailValue: raw ? parseNumber(raw) : 0 };
    case "fixed":
      return { fixedValue: raw ? parseNumber(raw) : 0 };
    case "deductive":
      return { deductiveBase: raw ? parseNumber(raw) : 0 };
    case "accumulative": {
      const parts = raw
        .split(";")
        .map((p) => p.trim())
        .filter(Boolean);
      if (parts.length < 2) {
        throw new Error(
          `Modo accumulative requiere base;perUnit[;penaltyPerUnit]: "${raw}"`,
        );
      }
      return {
        accumulativeBase: parseNumber(parts[0]),
        accumulativePerUnit: parseNumber(parts[1]),
        accumulativePenaltyPerUnit: parts[2] ? parseNumber(parts[2]) : 0,
      };
    }
    case "timing": {
      const parts = raw
        .split(";")
        .map((p) => p.trim())
        .filter(Boolean);
      if (parts.length === 0) {
        throw new Error(`Modo timing requiere bandas Etiqueta:puntos`);
      }
      // Sintaxis soportada por banda:
      //   Etiqueta:puntos                          (sin fecha — fallback)
      //   Etiqueta@YYYY-MM-DD HH:mm:puntos         (con fecha de corte)
      // El último ":" separa los puntos. Lo anterior es "etiqueta[@fecha]".
      const bands = parts.map((p) => {
        const lastColon = p.lastIndexOf(":");
        if (lastColon < 0) {
          throw new Error(
            `Banda timing inválida: "${p}" (usa Etiqueta:puntos o Etiqueta@fecha:puntos)`,
          );
        }
        const left = p.slice(0, lastColon).trim();
        const ptsRaw = p.slice(lastColon + 1).trim();
        if (!left || !ptsRaw) {
          throw new Error(`Banda timing inválida: "${p}"`);
        }
        const atIdx = left.indexOf("@");
        let label = left;
        let untilISO: string | undefined;
        if (atIdx > 0) {
          label = left.slice(0, atIdx).trim();
          const dateRaw = left.slice(atIdx + 1).trim();
          untilISO = parseDeadline(dateRaw);
        }
        const band: { label: string; points: number; untilDate?: Timestamp } = {
          label,
          points: parseNumber(ptsRaw),
        };
        if (untilISO) {
          band.untilDate = Timestamp.fromDate(new Date(untilISO));
        }
        return band;
      });
      return { timingBands: bands };
    }
  }
}

function truthy(s: string): boolean {
  return /^(1|true|si|sí|yes|y)$/i.test(s.trim());
}

/**
 * Parsea una fecha del CSV en formato `YYYY-MM-DD [HH:mm]` o ISO completa.
 * Devuelve la fecha como ISO string. Lanza si es inválida.
 */
function parseDeadline(raw: string): string | undefined {
  const s = raw.trim();
  if (!s) return undefined;
  const normalized = s.includes("T") ? s : s.replace(/\s+/, "T");
  const d = new Date(normalized);
  if (isNaN(d.getTime())) {
    throw new Error(`Fecha inválida: "${raw}" (usa YYYY-MM-DD HH:mm)`);
  }
  return d.toISOString();
}

function parseSubmissionCols(
  entregable: string,
  plantilla: string,
  opens: string,
  deadline: string,
  permiteTarde: string,
  notificar: string,
  lineNum: number,
): CsvActivityDraft["submission"] | undefined {
  const enabled = truthy(entregable);
  if (!enabled) return undefined;
  const tpl = plantilla.trim().toLowerCase();
  if (tpl && !(tpl in SUBMISSION_TEMPLATES)) {
    throw new Error(
      `Línea ${lineNum}: plantilla "${plantilla}" inválida. Opciones: ${Object.keys(
        SUBMISSION_TEMPLATES,
      ).join(", ")}.`,
    );
  }
  let opensISO: string | undefined;
  let deadlineISO: string | undefined;
  try {
    opensISO = parseDeadline(opens);
    deadlineISO = parseDeadline(deadline);
  } catch (e) {
    throw new Error(`Línea ${lineNum}: ${(e as Error).message}`);
  }
  if (opensISO && deadlineISO && new Date(opensISO) > new Date(deadlineISO)) {
    throw new Error(`Línea ${lineNum}: la apertura es posterior al deadline.`);
  }
  return {
    template: tpl || undefined,
    opensISO,
    deadlineISO,
    allowLate: truthy(permiteTarde),
    notifyOnOpen: truthy(notificar),
  };
}

export interface CsvCategoryDraft {
  name: string;
  description?: string;
  color?: string;
  order: number;
  scoreMode: ActivityScoreMode;
  scoreConfig: ActivityScoreConfig;
}

export interface CsvActivityDraft {
  categoryName: string;
  name: string;
  description?: string;
  order: number;
  /** Si vacío, hereda el modo y config de la categoría. */
  scoreMode?: ActivityScoreMode;
  scoreConfig?: ActivityScoreConfig;
  /** F4: si la actividad requiere entregable previo. */
  submission?: {
    template?: string;
    opensISO?: string;
    deadlineISO?: string;
    allowLate?: boolean;
    notifyOnOpen?: boolean;
  };
}

export interface CsvParseResult {
  categories: CsvCategoryDraft[];
  activities: CsvActivityDraft[];
  /** Errores no fatales: filas ignoradas con razón. */
  warnings: string[];
}

/**
 * Parsea un CSV pegado por el usuario y devuelve borradores listos para
 * importar. Lanza error si el CSV tiene problemas estructurales graves
 * (encabezado faltante, modo inválido en categoría, etc.).
 */
export function parseActivitiesCsv(csvText: string): CsvParseResult {
  const lines = csvText
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0 && !l.startsWith("#"));

  if (lines.length === 0) throw new Error("CSV vacío");

  // Header tolerante: opcional. Si la primera fila empieza por "tipo," la saltamos.
  const first = lines[0].toLowerCase();
  const hasHeader = first.startsWith("tipo,") || first.startsWith("tipo;");
  const dataLines = hasHeader ? lines.slice(1) : lines;

  const categories: CsvCategoryDraft[] = [];
  const activities: CsvActivityDraft[] = [];
  const warnings: string[] = [];

  let autoOrderCat = 10;
  const actOrderByCat = new Map<string, number>();

  dataLines.forEach((line, idx) => {
    const lineNum = hasHeader ? idx + 2 : idx + 1;
    const cols = parseCsvLine(line);
    if (cols.length < 2) {
      warnings.push(`Línea ${lineNum}: columnas insuficientes — ignorada.`);
      return;
    }
    const [
      tipoRaw,
      categoria,
      nombre,
      modoRaw,
      puntos,
      descripcion,
      color,
      ordenRaw,
      entregableRaw,
      plantillaRaw,
      aperturaRaw,
      deadlineRaw,
      permiteTardeRaw,
      notificarRaw,
    ] = [
      cols[0] ?? "",
      cols[1] ?? "",
      cols[2] ?? "",
      cols[3] ?? "",
      cols[4] ?? "",
      cols[5] ?? "",
      cols[6] ?? "",
      cols[7] ?? "",
      cols[8] ?? "",
      cols[9] ?? "",
      cols[10] ?? "",
      cols[11] ?? "",
      cols[12] ?? "",
      cols[13] ?? "",
    ];

    const tipo = tipoRaw.toUpperCase();
    if (tipo !== "C" && tipo !== "A") {
      warnings.push(
        `Línea ${lineNum}: tipo "${tipoRaw}" inválido (use C o A).`,
      );
      return;
    }
    if (!categoria) {
      warnings.push(`Línea ${lineNum}: categoría vacía — ignorada.`);
      return;
    }

    const order = ordenRaw ? Number(ordenRaw) : NaN;

    if (tipo === "C") {
      const modo = (modoRaw || "podium").toLowerCase() as ActivityScoreMode;
      if (!VALID_MODES.includes(modo)) {
        throw new Error(
          `Línea ${lineNum}: modo "${modoRaw}" inválido para categoría "${categoria}".`,
        );
      }
      let config: ActivityScoreConfig;
      try {
        config = buildScoreConfig(modo, puntos);
      } catch (e) {
        throw new Error(
          `Línea ${lineNum} (categoría "${categoria}"): ${(e as Error).message}`,
        );
      }
      categories.push({
        name: categoria,
        description: descripcion || undefined,
        color: color || undefined,
        order: Number.isFinite(order) ? order : autoOrderCat,
        scoreMode: modo,
        scoreConfig: config,
      });
      autoOrderCat += 10;
    } else {
      // Actividad
      if (!nombre) {
        warnings.push(`Línea ${lineNum}: actividad sin nombre — ignorada.`);
        return;
      }
      let scoreMode: ActivityScoreMode | undefined;
      let scoreConfig: ActivityScoreConfig | undefined;
      if (modoRaw) {
        const modo = modoRaw.toLowerCase() as ActivityScoreMode;
        if (!VALID_MODES.includes(modo)) {
          throw new Error(
            `Línea ${lineNum}: modo "${modoRaw}" inválido para actividad "${nombre}".`,
          );
        }
        scoreMode = modo;
        try {
          scoreConfig = buildScoreConfig(modo, puntos);
        } catch (e) {
          throw new Error(
            `Línea ${lineNum} (actividad "${nombre}"): ${(e as Error).message}`,
          );
        }
      }
      const nextOrder = (actOrderByCat.get(categoria) ?? 0) + 10;
      actOrderByCat.set(categoria, nextOrder);
      const submission = parseSubmissionCols(
        entregableRaw,
        plantillaRaw,
        aperturaRaw,
        deadlineRaw,
        permiteTardeRaw,
        notificarRaw,
        lineNum,
      );
      activities.push({
        categoryName: categoria,
        name: nombre,
        description: descripcion || undefined,
        order: Number.isFinite(order) ? order : nextOrder,
        scoreMode,
        scoreConfig,
        submission,
      });
    }
  });

  if (categories.length === 0 && activities.length === 0) {
    throw new Error("CSV sin filas válidas.");
  }
  // Validar que toda actividad tenga su categoría declarada en el CSV
  // o exista una sembrada previamente — esto se valida al importar.
  return { categories, activities, warnings };
}

export interface CsvImportSummary {
  categoriesCreated: number;
  activitiesCreated: number;
  skipped: string[];
}

/**
 * Construye `submissionRequirements` para upsertActivity a partir de la
 * configuración mínima del CSV (entregable, plantilla, deadline, allowLate).
 */
function buildSubmissionRequirementsFromCsv(
  sub: CsvActivityDraft["submission"],
): EventActivity["submissionRequirements"] | undefined {
  if (!sub) return undefined;
  const tplKey = (sub.template ?? "single_file") as SubmissionTemplateKey;
  const tpl = SUBMISSION_TEMPLATES[tplKey];
  const req: EventActivity["submissionRequirements"] = {
    enabled: true,
    fields: tpl.fields,
    allowLate: !!sub.allowLate,
    notifyOnOpen: !!sub.notifyOnOpen,
  };
  if (sub.deadlineISO) {
    req.deadline = Timestamp.fromDate(new Date(sub.deadlineISO));
  }
  if (sub.opensISO) {
    req.opensAt = Timestamp.fromDate(new Date(sub.opensISO));
  }
  return req;
}

/**
 * Importa categorías + actividades a partir de un CSV ya parseado.
 *
 * Comportamiento:
 *  - Crea (o actualiza por slug) cada categoría.
 *  - Para cada actividad, busca la categoría por nombre dentro de las
 *    creadas en este CSV; si no existe, intenta usar `existingCategories`.
 *  - Si una actividad no tiene `scoreMode`, hereda el de su categoría.
 */
export async function importActivitiesFromCsv(
  eventId: string,
  parsed: CsvParseResult,
  existingCategories: EventActivityCategory[] = [],
): Promise<CsvImportSummary> {
  const summary: CsvImportSummary = {
    categoriesCreated: 0,
    activitiesCreated: 0,
    skipped: [],
  };

  // Mapa nombre normalizado -> { id, mode, config }
  const categoryMap = new Map<
    string,
    { id: string; mode: ActivityScoreMode; config: ActivityScoreConfig }
  >();
  for (const c of existingCategories) {
    categoryMap.set(slugify(c.name), {
      id: c.id,
      mode: c.defaultScoreMode,
      config: c.defaultScoreConfig,
    });
  }

  // 1) Categorías
  for (const cat of parsed.categories) {
    const id = await upsertCategory({
      eventId,
      name: cat.name,
      description: cat.description,
      color: cat.color,
      order: cat.order,
      defaultScoreMode: cat.scoreMode,
      defaultScoreConfig: cat.scoreConfig,
      active: true,
    });
    categoryMap.set(slugify(cat.name), {
      id,
      mode: cat.scoreMode,
      config: cat.scoreConfig,
    });
    summary.categoriesCreated++;
  }

  // 2) Actividades
  for (const act of parsed.activities) {
    const key = slugify(act.categoryName);
    const cat = categoryMap.get(key);
    if (!cat) {
      summary.skipped.push(
        `Actividad "${act.name}": categoría "${act.categoryName}" no encontrada.`,
      );
      continue;
    }
    const mode = act.scoreMode ?? cat.mode;
    const config = act.scoreConfig ?? cat.config;
    const submissionRequirements = buildSubmissionRequirementsFromCsv(
      act.submission,
    );
    await upsertActivity({
      eventId,
      categoryId: cat.id,
      name: act.name,
      description: act.description,
      scoreMode: mode,
      scoreConfig: config,
      participants: { mode: "team" },
      order: act.order,
      active: true,
      submissionRequirements,
    });
    summary.activitiesCreated++;
  }

  return summary;
}
